import { Schema } from "effect";
import type { Scope } from "effect";
import type { HistoryTag, HistoryRevision } from "../../api/history/model.ts";

export const HistorySelection = Schema.Struct({ stationId: Schema.NullOr(Schema.String), workspaceId: Schema.NullOr(Schema.String), collapsed: Schema.Boolean, refreshToken: Schema.optionalKey(Schema.Number) });
export type HistorySelection = typeof HistorySelection.Type;
const MutationIdentity = { selectionKey: Schema.String, selectionEpoch: Schema.Number };
export const HistoryAction = Schema.Union([
  Schema.TaggedStruct("Select", HistorySelection.fields), Schema.TaggedStruct("Refresh", {}),
  Schema.TaggedStruct("CreateTag", MutationIdentity),
  Schema.TaggedStruct("DeleteTag", { ...MutationIdentity, name: Schema.String }),
  Schema.TaggedStruct("RestoreTag", { ...MutationIdentity, name: Schema.String }),
  Schema.TaggedStruct("RestoreRevision", { ...MutationIdentity, revision: Schema.Number, path: Schema.optionalKey(Schema.String) })
]);
export type HistoryAction = typeof HistoryAction.Type;
export type HistoryMutationAction = Extract<HistoryAction, { readonly selectionEpoch: number }>;
export const HistoryMutationKind = Schema.Literals(["create-tag", "delete-tag", "restore-tag", "restore-revision"]);
export type HistoryMutationKind = typeof HistoryMutationKind.Type;
export const HistorySettlement = Schema.Struct({ selectionKey: Schema.String, selectionEpoch: Schema.Number, action: HistoryMutationKind, outcome: Schema.Literals(["success", "refused", "cancelled"]), progress: Schema.Literals(["complete", "uncertain", "not-started"]), message: Schema.NullOr(Schema.String) });
export type HistorySettlement = typeof HistorySettlement.Type;
export interface HistoryData { readonly tags: readonly HistoryTag[]; readonly revisions: readonly HistoryRevision[]; readonly stationUpdatedAt: number; readonly workspaceUpdatedAt: number }
export interface HistoryInteraction { readonly identity: object; readonly scope: Scope.Closeable; readonly active: boolean; readonly epoch: number }
export interface HistoryPending { readonly identity: object; readonly epoch: number; readonly action: HistoryMutationKind }
export interface HistoryState {
  readonly closed: boolean;
  readonly selection: HistorySelection | null;
  readonly epoch: number;
  readonly request: object | null;
  readonly data: HistoryData | null;
  readonly queryLoading: boolean;
  readonly loaded: boolean;
  readonly error: string | null;
  readonly pending: ReadonlyMap<string, HistoryPending>;
  readonly interaction: HistoryInteraction | null;
}
export interface HistorySnapshot {
  readonly stationId: string | null;
  readonly workspaceId: string | null;
  readonly selectionKey: string;
  readonly selectionEpoch: number;
  readonly data: HistoryData | null;
  readonly loading: boolean;
  readonly mutating: boolean;
  readonly loaded: boolean;
  readonly error: string | null;
}
export const historySelectionKey = (selection: Pick<HistorySelection, "stationId" | "workspaceId"> | null): string => JSON.stringify([selection?.stationId ?? null, selection?.workspaceId ?? null]);
export const emptyHistoryState = (): HistoryState => ({ closed: false, selection: null, epoch: 0, request: null, data: null, queryLoading: false, loaded: false, error: null, pending: new Map(), interaction: null });
export const historySnapshot = (state: HistoryState): HistorySnapshot => {
  const key = historySelectionKey(state.selection), pending = state.pending.has(key);
  return { stationId: state.selection?.stationId ?? null, workspaceId: state.selection?.workspaceId ?? null, selectionKey: key, selectionEpoch: state.epoch, data: state.data, loading: state.queryLoading || pending, mutating: pending || (state.interaction?.active === true && state.interaction.epoch === state.epoch), loaded: state.loaded, error: state.error };
};
export const shortHistoryDate = (timestamp: number): string => new Date(timestamp).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
